<template>
  <sc-flex :is-column="true">
    <WarningDialog
      :opened="unreviewedFormsWarningDialogOpened"
      :allow-skip="true"
      warning-title="Some pages are not reviewed"
      warning-text='There are some pages that you did not review. Please press "Close" to review the highlighted pages in red or press "Skip" to proceed without reviewing.'
      @close="unreviewedFormsWarningDialogOpened = false"
      @skip="finishReviewing(true)"
    />

    <WarningDialog
      :opened="erroredFormsWarningDialogOpened"
      :allow-skip="false"
      warning-title="Some pages need to be corrected"
      warning-text='There are some fields that are invalid or empty. Please press "Close" to review the highlighted pages below in red.'
      @close="erroredFormsWarningDialogOpened = false"
    />

    <SignaturePadDialog
      :opened.sync="signaturePadOpened"
      :signature-type.sync="signatureType"
      :image-signature.sync="imageSignature"
      :text-signature.sync="textSignature"
      :saving="savingSignature"
      @saveSignature="saveSignature"
    />

    <!-- Partial: Alerts -->
    <sc-alert v-if="taxReturn.status === 'action_required'" type="info">
      <b>Instruction:</b> Please review every e-signature box placements and
      adjust as necessary.
      <sc-button-group slot="button-group">
        <sc-button
          type="ghost"
          @click="finishReviewing()"
          icon-right="arrow-right"
          :is-disabled="saving || pdfLoading"
        >
          Finished Reviewing
        </sc-button>
      </sc-button-group>
    </sc-alert>

    <ReadonlyAlert
      v-else
      :tax-return-status="taxReturn.status"
      :disabled="pdfLoading"
      @next="next"
    />

    <sc-flex>
      <!-- Partial: Sidebar -->
      <sc-flex class="sidebar" :is-column="true" :gap="8">
        <sc-h5>E-Sign Forms</sc-h5>
        <ul class="page-list">
          <li
            v-for="(form, index) in taxReturn.e_sign_forms"
            :key="form.page"
            :class="{
              selected: currentFormIndex === index + 1,
              unreviewed: shouldHighlightUnreviewedForms && !form.reviewed_by,
              errored: shouldHighlightErroredForms && isFormInvalid(form),
            }"
          >
            <a @click="currentFormIndex = index + 1">
              <v-layout>
                <v-icon class="mr-1">
                  {{
                    shouldHighlightErroredForms && isFormInvalid(form)
                      ? 'error'
                      : 'bookmark_border'
                  }}
                </v-icon>

                <span v-if="form.form_id === 'other'">
                  <span v-if="!isEmpty(form.custom_form_name)">
                    {{ form.custom_form_name }}
                  </span>
                  <span v-else>
                    Other
                  </span>
                </span>
                <span v-else-if="getFormDefinitionById(form.form_id)">
                  {{ getFormDefinitionById(form.form_id).name }} ({{
                    form.form_id
                  }})
                </span>
                <span v-else>
                  Unknown
                  <span
                    v-if="form.status === 'analyzing'"
                    class="grey--text body-1"
                  >
                    (analyzing...)
                  </span>
                </span>
              </v-layout>
            </a>
          </li>
        </ul>
      </sc-flex>

      <!-- Partial: Content -->
      <div class="pdf-container">
        <!-- Partial: Toolbar -->
        <nav class="toolbar">
          <sc-flex class="toolbar__pageNavigation" :is-centered="true" :gap="8">
            <sc-button-group>
              <sc-button
                type="secondary"
                @click="currentFormIndex -= 1"
                :is-disabled="currentFormIndex === 1"
                :is-icon-only="true"
                icon-left="arrow-left"
                size="small"
              ></sc-button>
            </sc-button-group>
            <sc-flex :is-y-centered="true" :gap="8">
              <sc-small>Page:</sc-small>
              <input
                :min="1"
                :max="formCount"
                type="number"
                :value="currentFormIndex"
                @change="updateCurrentFormIndex($event)"
              />
              <sc-small>/ {{ formCount }}</sc-small>
            </sc-flex>
            <sc-button-group>
              <sc-button
                type="secondary"
                @click="currentFormIndex += 1"
                :is-disabled="currentFormIndex === formCount"
                :is-icon-only="true"
                icon-right="arrow-right"
                size="small"
              >
              </sc-button>
            </sc-button-group>
          </sc-flex>

          <sc-flex
            class="toolbar__pageActions"
            x-align="right"
            :is-y-centered="true"
            :gap="8"
          >
            <sc-button
              v-if="
                taxReturn.filing_instructions &&
                  taxReturn.filing_instructions.length > 0
              "
              type="primary"
              size="small"
              @click="$emit('open-filing-instructions')"
            >
              View Filing Instructions
            </sc-button>

            <v-menu
              transition="slide-y-transition"
              bottom
              left
              nudge-bottom="30"
              class="my-1 toolbar__downloadMenu"
            >
              <sc-button-group slot="activator">
                <sc-button type="primary" size="small" icon-left="caret-down">
                  Download
                </sc-button>
              </sc-button-group>

              <v-list>
                <v-list-tile @click="download('current-form-only')">
                  <v-list-tile-title>
                    Download the current page only
                  </v-list-tile-title>
                </v-list-tile>

                <v-list-tile @click="download('signed-only')">
                  <v-list-tile-title>
                    Download forms containing signatures
                  </v-list-tile-title>
                </v-list-tile>

                <v-list-tile @click="download('all')">
                  <v-list-tile-title>
                    Download all forms (signed and unsigned)
                  </v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
          </sc-flex>
        </nav>

        <!-- Partial: Content -->
        <v-layout class="page-container">
          <!-- Partial: Scroll -->
          <v-layout class="scrollable-container">
            <v-layout class="pdf-page-container">
              <v-layout v-if="hasWetSignatures" column class="my-3 mx-3">
                <v-layout class="my-2">
                  <h3>
                    The client uploaded the following documents containing wet
                    signature(s):
                  </h3>
                </v-layout>

                <SignedDocumentList
                  :tax-return="taxReturn"
                  :current-company="currentCompany"
                  :current-form="currentForm"
                />
              </v-layout>

              <v-divider v-if="hasWetSignatures" class="my-2"></v-divider>
              <span class="body-2 grey--text mb-2" v-if="hasWetSignatures"
                >ORIGINAL FORM</span
              >

              <v-layout
                v-if="pdfLoading"
                class="mt-4"
                justify-center
                align-center
              >
                <v-progress-circular indeterminate color="primary" />
              </v-layout>

              <div id="pdf-annotation" class="pdf ph-no-capture" />
              <v-layout
                v-if="pdfLoaded && !currentForm.wetSignaturesRequired"
                class="main-signature-container"
                v-for="(signature, index) in currentForm.signatures"
                :key="index"
              >
                <!-- Signature Container -->
                <v-layout
                  class="signature-container"
                  :data-signatureid="index"
                  :style="
                    getStylePosition({
                      pageWidth: signature.pageWidth,
                      elementBottom: signature.signatureBottom,
                      elementLeft: signature.signatureLeft,
                    })
                  "
                >
                  <!-- Signature: Image -->
                  <img
                    v-if="signature.signatureType === 'image'"
                    :src="signature.signatureData"
                    :style="
                      getStyleDimension({
                        pageWidth: signature.pageWidth,
                        elementWidth: signature.signatureWidth,
                        elementHeight: signature.signatureHeight,
                      })
                    "
                  />

                  <!-- Signature: Text -->
                  <v-layout
                    v-else-if="signature.signatureType === 'text'"
                    align-center
                    justify-center
                    :style="
                      getStyleDimension({
                        pageWidth: signature.pageWidth,
                        elementWidth: signature.signatureWidth,
                        elementHeight: signature.signatureHeight,
                      })
                    "
                  >
                    <span
                      class="signature-text"
                      :style="
                        getStyleSignatureText({
                          pageWidth: signature.pageWidth,
                          fontSize: signature.signatureTextSize,
                        })
                      "
                    >
                      {{ signature.signatureData }}
                    </span>
                  </v-layout>

                  <!-- Signature: Area -->
                  <v-layout
                    v-else
                    class="sign-area"
                    :style="
                      getStyleDimension({
                        pageWidth: signature.pageWidth,
                        elementWidth: signature.signatureWidth,
                        elementHeight: signature.signatureHeight,
                      })
                    "
                  >
                    <div
                      class="signer-title"
                      :style="
                        getStyleForSignatureTitle({
                          pageWidth: signature.pageWidth,
                          fontSize: signature.signatureTextSize,
                        })
                      "
                    >
                      {{ signature.title }}
                      <span v-if="signature.name">({{ signature.name }})</span>
                    </div>
                    <h2
                      :style="
                        getStyleForSignatureHeader({
                          pageWidth: signature.pageWidth,
                          fontSize: signature.signatureTextSize,
                        })
                      "
                    >
                      Signature
                      <v-tooltip bottom>
                        <v-btn
                          slot="activator"
                          v-if="signature.title === 'Yourself'"
                          icon
                          small
                          @click="openSignDialog(index)"
                        >
                          <v-icon>edit</v-icon>
                        </v-btn>
                        <span>
                          Sign as yourself
                        </span>
                      </v-tooltip>
                    </h2>
                  </v-layout>
                </v-layout>

                <!-- Signature Date Container -->
                <v-layout
                  class="signature-date-container"
                  :data-signatureid="index"
                  :style="
                    getStylePosition({
                      pageWidth: signature.pageWidth,
                      elementBottom: signature.signatureDateBottom,
                      elementLeft: signature.signatureDateLeft,
                    })
                  "
                >
                  <v-layout
                    v-if="signature.signatureDate"
                    align-center
                    justify-center
                    :style="
                      getStyleDimension({
                        pageWidth: signature.pageWidth,
                        elementWidth: signature.signatureDateWidth,
                        elementHeight: signature.signatureDateHeight,
                      })
                    "
                  >
                    <span
                      class="signature-date"
                      :style="
                        getStyleSignatureDateText({
                          pageWidth: signature.pageWidth,
                          fontSize: signature.signatureTextSize,
                        })
                      "
                    >
                      {{ signature.signatureDate }}
                    </span>
                  </v-layout>

                  <v-layout
                    v-else
                    class="sign-area"
                    :style="
                      getStyleDimension({
                        pageWidth: signature.pageWidth,
                        elementWidth: signature.signatureDateWidth,
                        elementHeight: signature.signatureDateHeight,
                      })
                    "
                  >
                    <div
                      class="signer-title"
                      :style="
                        getStyleForSignatureTitle({
                          pageWidth: signature.pageWidth,
                          fontSize: signature.signatureTextSize,
                        })
                      "
                    >
                      {{ signature.title }}
                      <span v-if="signature.name">({{ signature.name }})</span>
                    </div>
                    <h2
                      :style="
                        getStyleForSignatureHeader({
                          pageWidth: signature.pageWidth,
                          fontSize: signature.signatureTextSize,
                        })
                      "
                    >
                      Date
                    </h2>
                  </v-layout>
                </v-layout>
              </v-layout>
            </v-layout>
          </v-layout>

          <!-- Partial: Sidebar -->
          <div class="sidebar--right">
            <v-form ref="form">
              <h3>Please review signing details below for this form.</h3>

              <div class="mt-3" v-if="currentForm">
                <h3 class="font-weight-bold">Form type:</h3>

                <v-autocomplete
                  solo
                  hide-details
                  class="mt-1 select-input"
                  :items="formDefinitions"
                  :item-text="
                    item => item.name + (item.id ? ' - ' + item.id : '')
                  "
                  item-value="id"
                  :value="currentForm.form_id"
                  @change="currentForm.form_id = $event"
                  :disabled="!editable"
                  :rules="[rules.required]"
                />
              </div>

              <div
                class="mt-3"
                v-if="currentForm && currentForm.form_id === 'other'"
              >
                <h3 class="font-weight-bold">Form name:</h3>

                <v-combobox
                  solo
                  hide-details
                  clearable
                  class="mt-1 select-input"
                  :items="customFormNames"
                  item-text="name"
                  item-value="name"
                  :value="currentForm.custom_form_name"
                  :append-icon="null"
                  :disabled="!editable"
                  :rules="[rules.required]"
                  @change="handleCustomFormChange($event)"
                >
                  <v-layout
                    slot="item"
                    slot-scope="props"
                    align-center
                    justify-space-between
                  >
                    <span>
                      {{ props.item.name }}
                    </span>

                    <v-btn
                      icon
                      ripple
                      @click.prevent.stop="deleteCustomFormName(props.item)"
                    >
                      <v-icon
                        color="error lighten-1"
                        class="material-icons-outlined"
                        >delete</v-icon
                      >
                    </v-btn>
                  </v-layout>
                </v-combobox>
              </div>

              <div class="mt-4" v-if="currentForm">
                <h3 class="font-weight-bold">Signers:</h3>

                <div
                  v-for="(signature, index) in currentForm.signatures"
                  :key="index"
                  class="signer-container"
                >
                  <v-layout justify-space-between class="mb-2 font-weight-bold">
                    <span>Signer {{ index + 1 }}</span>
                    <v-icon
                      small
                      color="error"
                      class="material-icons-outlined"
                      @click="removeSigner(index)"
                      v-if="editable"
                    >
                      remove_circle
                    </v-icon>
                  </v-layout>

                  <v-layout align-center class="form-control">
                    <label class="label"
                      >Title: <span class="error--text">*</span></label
                    >

                    <v-select
                      solo
                      hide-details
                      :items="selectableTitles"
                      v-model="signature.title"
                      :disabled="!editable"
                      :rules="[rules.required]"
                      @change="handleTitleChange($event, signature, index)"
                    />
                  </v-layout>

                  <v-layout align-center class="form-control">
                    <label class="label"
                      >Name: <span class="error--text">*</span></label
                    >

                    <v-select
                      v-if="signature.title !== 'Yourself'"
                      solo
                      hide-details
                      v-model="signature.name"
                      placeholder="Select signer"
                      :items="selectableNames"
                      :disabled="!editable"
                      :rules="[rules.required]"
                    />

                    <span v-else>{{ currentUser.full_name }}</span>
                  </v-layout>

                  <v-layout
                    align-center
                    class="form-control"
                    v-if="signature.title !== 'Yourself'"
                  >
                    <label class="label"
                      >Email: <span class="error--text">*</span></label
                    >

                    <v-select
                      solo
                      hide-details
                      v-model="signature.userId"
                      placeholder="Select email to send to"
                      :items="selectableOwners"
                      :item-text="item => item.email"
                      :item-value="item => item.id"
                      :disabled="!editable"
                      :rules="[rules.required]"
                    />
                  </v-layout>
                </div>

                <v-btn
                  color="primary"
                  class="ma-0 mt-2 text-none"
                  :disabled="!editable || pdfLoading"
                  @click="addSigner"
                >
                  <v-icon>add</v-icon>
                  Add signer
                </v-btn>
              </div>
            </v-form>
          </div>
        </v-layout>
      </div>
    </sc-flex>
  </sc-flex>
</template>

<script>
import pdf from 'vue-pdf';
import API from '@/api';
import interact from 'interactjs';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import chunk from 'lodash/chunk';
import uniqBy from 'lodash/uniqBy';
import { mapGetters } from 'vuex';
import { PDFDocument, rgb } from 'pdf-lib';
import axios from 'axios';
import ReadonlyAlert from '@/components/returns/ReadonlyAlert';
import download from 'downloadjs';
import fontkit from '@pdf-lib/fontkit';
import SignedDocumentList from '@/components/returns/SignedDocumentList';
import WarningDialog from '@/components/returns/WarningDialog';
import JSZip from 'jszip';
import UtilsMixin from '@/mixins/UtilsMixins';
import { Sema } from 'async-sema';
import SignaturePadDialog from '@/components/dialogs/SignaturePadDialog';
import moment from 'moment';

export default {
  components: {
    pdf,
    ReadonlyAlert,
    SignedDocumentList,
    WarningDialog,
    SignaturePadDialog,
  },

  mixins: [UtilsMixin],

  props: {
    currentCompany: {
      type: Object,
      default: () => {},
    },

    taxReturn: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      currentFormIndex: 1,

      pdfData: null,
      pdfDoc: null,
      pdfLoading: false,
      pdfProgress: 0,
      pdfLoaded: false,
      pdfPageIndex: 1,
      pdfPageWidth: 0,
      pdfPageHeight: 0,

      saving: false,

      // States for forms with errors
      erroredFormsWarningDialogOpened: false,
      shouldHighlightErroredForms: false,

      // States for unreviewed forms
      unreviewedFormsWarningDialogOpened: false,
      shouldHighlightUnreviewedForms: false,

      formDefinitions: [],
      selectableTitles: ['Taxpayer', 'Spouse', 'Yourself'],
      customFormNames: [],

      rules: {
        required: value => !!value || 'Required.',
      },

      signaturePadOpened: false,
      signatureType: 'image',
      imageSignature: '',
      textSignature: '',
      savingSignature: false,
      currentSignatureIndex: null,
      savedSignaturesByName: {},
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentUserFirm: 'currentFirm',
    }),

    hasWetSignatures() {
      return (
        this.currentForm.wetSignatures &&
        this.currentForm.wetSignatures.length > 0
      );
    },

    currentForm() {
      return this.taxReturn.e_sign_forms[this.currentFormIndex - 1];
    },

    formCount() {
      return this.taxReturn.e_sign_forms.length;
    },

    selectableNames() {
      return this.currentCompany.all_owners.map(owner => owner.full_name);
    },

    selectableOwners() {
      return this.currentCompany.owners.filter(owner => owner.email);
    },
    editable() {
      return this.taxReturn.status === 'action_required';
    },
  },

  methods: {
    isEmpty,

    moment,

    openSignDialog(signatureIndex) {
      this.signaturePadOpened = true;
      this.currentSignatureIndex = signatureIndex;

      const signature = this.currentForm.signatures[signatureIndex];
      const savedSignature = this.savedSignaturesByName[signature.name];

      this.signatureType = savedSignature
        ? savedSignature.signatureType
        : 'image';
      this.imageSignature =
        savedSignature && savedSignature.signatureType === 'image'
          ? savedSignature.signatureData
          : '';
      this.textSignature =
        savedSignature && savedSignature.signatureType === 'text'
          ? savedSignature.signatureData
          : '';
    },

    handleTitleChange(title, signature, index) {
      if (title === 'Yourself') {
        signature.name = this.currentUser.full_name;
        signature.userId = this.currentUser.id;
        this.openSignDialog(index);
      } else {
        if (this.currentCompany.owners.length === 1) {
          signature.name = this.currentCompany.owners[0].full_name;
          signature.userId = this.currentCompany.owners[0].id;
        } else {
          signature.name = '';
          signature.userId = null;
        }
      }
    },

    async saveSignature() {
      try {
        this.savingSignature = true;

        const previouslySavedTaxReturn = await this.save();
        this.$emit('on-save-signature', previouslySavedTaxReturn);

        const signature = this.currentForm.signatures[
          this.currentSignatureIndex
        ];

        // For some reason, some browsers do not return the correct image data
        // This is rare and we need to prevent storing invalid image data
        if (
          this.signatureType === 'image' &&
          !this.imageSignature &&
          !this.imageSignature.startsWith('data:image/png;base64,')
        ) {
          this.$store.dispatch('application/setErrorNotification', {
            message: "We couldn't capture your signature. Please try again.",
          });
          return;
        }

        this.savedSignaturesByName[signature.name] = {
          signatureType: this.signatureType,
          signatureData:
            this.signatureType === 'image'
              ? this.imageSignature
              : this.textSignature,
        };

        // Ensure date is set
        const currentDate = moment().format('L');
        signature.signatureDate = currentDate;
        signature.signatureData =
          this.signatureType === 'image'
            ? this.imageSignature
            : this.textSignature;
        signature.signatureType = this.signatureType;

        const response = await API.taxReturn.saveSignature({
          id: this.taxReturn.id,
          companyId: this.currentCompany.id,
          formIndex: this.currentFormIndex - 1,
          signatureIndex: this.currentSignatureIndex,
          signatureType: this.signatureType,
          signatureData:
            this.signatureType === 'image'
              ? this.imageSignature
              : this.textSignature,
          signatureDate: currentDate,
          sessionId: null,
          userId: signature.userId,
          title: signature.title,
          name: signature.name,
          email: signature.email,
          signatureLeft: signature.signatureLeft,
          signatureBottom: signature.signatureBottom,
          signatureWidth: signature.signatureWidth,
          signatureHeight: signature.signatureHeight,
          signatureDateLeft: signature.signatureDateLeft,
          signatureDateBottom: signature.signatureDateBottom,
          signatureDateWidth: signature.signatureDateWidth,
          signatureDateHeight: signature.signatureDateHeight,
        });

        const taxReturn = response.data;

        this.$emit('on-save-signature', taxReturn);

        this.renderPdfWithAnnotations();
      } catch (error) {
        console.log(error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem signing the document.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.savingSignature = false;
        this.signaturePadOpened = false;
      }
    },

    async analyzeAllESignForms() {
      const formsToAnalyze = this.taxReturn.e_sign_forms
        .filter(
          form => form.form_id === 'unknown' && form.status !== 'analyzed',
        )
        .map(form => {
          form.status = 'analyzing';
          return form;
        });

      const sema = new Sema(4); // Limit the number of concurrent requests

      const analyzeForm = async form => {
        await sema.acquire();

        const pages = [form.page];

        try {
          const response = await axios.post(
            `/tax_returns/${this.taxReturn.id}/analyze_e_sign_forms`,
            {
              company_id: this.currentCompany.id,
              pages,
            },
          );

          response.data.forEach(result => {
            if (form.page === result.page) {
              form.form_id = result.form_id;
              form.form_name = result.form_name;
              form.signatures = result.signatures;
              form.status = 'analyzed';
            }
          });
        } catch (error) {
          form.status = 'analyze_failed';
        } finally {
          sema.release();
        }
      };

      const analyzeTasks = formsToAnalyze.map(form => analyzeForm(form));

      await Promise.all(analyzeTasks);
    },

    async updateCurrentFormIndex($event) {
      let newIndex = parseInt($event.target.value);

      // Make sure the index is not out of bounds
      if (newIndex < 1) {
        newIndex = 1;
      } else if (newIndex > this.formCount) {
        newIndex = this.formCount;
      }

      this.currentFormIndex = newIndex;
      $event.target.value = newIndex;
      await this.renderPdfWithAnnotations();
    },

    async getFormDefinitions() {
      const response = await API.signatureRequest.getFormDefinitions();

      this.formDefinitions = response.data.concat({
        id: 'other',
        name: 'Other (custom)',
        taxpayer_signature: null,
        spouse_signature: null,
      });
    },

    getFormDefinitionById(id) {
      return this.formDefinitions.find(form => form.id === id);
    },

    addSigner() {
      const baseWidth = 918;
      const { width: pageWidth, height: pageHeight } = {
        width: this.pdfPageWidth,
        height: this.pdfPageHeight,
      };

      const signatureWidth = baseWidth * 0.25; // ~153pt
      const signatureHeight = baseWidth * 0.06; // ~37pt
      const signatureDateWidth = baseWidth * 0.15; // ~92pt
      const signatureDateHeight = baseWidth * 0.06; // ~37pt
      const signatureTextSize = baseWidth * 0.032; // ~20pt
      const indexOffset = this.currentForm.signatures.length * 80;

      // put in middle of page
      const signatureLeft = (pageWidth - signatureWidth) / 2 - 100;
      const signatureBottom = pageHeight / 2 - indexOffset;
      const signatureDateLeft = (pageWidth - signatureDateWidth) / 2 + 100;
      const signatureDateBottom = pageHeight / 2 - indexOffset;

      this.currentForm.signatures.push({
        title: 'Taxpayer',
        name:
          this.currentCompany.owners.length === 1
            ? this.currentCompany.owners[0].full_name
            : '',
        userId:
          this.currentCompany.owners.length === 1
            ? this.currentCompany.owners[0].id
            : null,
        pageWidth,
        pageHeight,
        signatureWidth,
        signatureHeight,
        signatureBottom,
        signatureLeft,
        signatureDateWidth,
        signatureDateHeight,
        signatureDateLeft,
        signatureDateBottom,
        signatureTextSize,
      });

      this.makeDraggable();
      this.save(true);
    },

    removeSigner(index) {
      this.currentForm.signatures.splice(index, 1);
      this.save(true);
    },

    getPageRatio(pageWidth) {
      if (this.pdfPageWidth === null) {
        return 1;
      }

      const ratio = this.pdfPageWidth / pageWidth;
      const maxRatio = 1.5;

      return Math.min(ratio, maxRatio);
    },

    getStylePosition({ pageWidth, elementBottom, elementLeft }) {
      const ratio = this.getPageRatio(pageWidth);

      return {
        bottom: elementBottom * ratio + 'px',
        left: elementLeft * ratio + 'px',
      };
    },

    getStyleDimension({ pageWidth, elementWidth, elementHeight }) {
      const ratio = this.getPageRatio(pageWidth);

      return {
        width: elementWidth * ratio + 'px',
        height: elementHeight * ratio + 'px',
      };
    },

    getStyleSignatureText({ pageWidth, fontSize }) {
      const ratio = this.getPageRatio(pageWidth);

      return {
        fontSize: fontSize * ratio + 'px',
      };
    },

    getStyleSignatureDateText({ pageWidth, fontSize }) {
      const ratio = this.getPageRatio(pageWidth);

      return {
        fontSize: (fontSize / 2) * ratio + 'px',
      };
    },

    getStyleForSignatureHeader({ pageWidth, fontSize }) {
      const ratio = this.getPageRatio(pageWidth);

      return {
        fontSize: (fontSize / 1.5) * ratio + 'px',
      };
    },

    getStyleForSignatureTitle({ pageWidth, fontSize }) {
      const ratio = this.getPageRatio(pageWidth);

      return {
        fontSize: (fontSize / 2) * ratio + 'px',
      };
    },

    makeDraggable() {
      interact('.signature-container').draggable({
        listeners: {
          move: event => {
            const target = event.target;
            const x =
              (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
            const y =
              (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;

            event.target.style.transform = `translate(${x}px, ${y}px)`;
            target.setAttribute('data-x', x);
            target.setAttribute('data-y', y);
          },

          end: event => {
            const index = parseInt(event.target.dataset['signatureid']);
            const signature = this.currentForm.signatures[index];

            const target = event.target;
            const pageRatio = this.getPageRatio(signature.pageWidth);

            const deltaLeft =
              (parseFloat(target.getAttribute('data-x')) || 0) / pageRatio;
            const deltaBottom =
              (parseFloat(target.getAttribute('data-y')) || 0) / pageRatio;

            const newLeft = signature.signatureLeft + deltaLeft;
            const newBottom = signature.signatureBottom - deltaBottom;

            signature.signatureLeft = newLeft;
            signature.signatureBottom = newBottom;

            target.setAttribute('data-x', 0);
            target.setAttribute('data-y', 0);
            target.style.transform = 'none';

            if (this.currentForm.form_id === 'other') {
              this.saveCustomFormSignatures(this.currentForm.signatures);
            }

            this.save(true);
          },
        },
      });

      interact('.signature-date-container').draggable({
        listeners: {
          move: event => {
            const target = event.target;
            const x =
              (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
            const y =
              (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;

            event.target.style.transform = `translate(${x}px, ${y}px)`;
            target.setAttribute('data-x', x);
            target.setAttribute('data-y', y);
          },

          end: event => {
            const index = parseInt(event.target.dataset['signatureid']);
            const signature = this.currentForm.signatures[index];

            const target = event.target;
            const pageRatio = this.getPageRatio(signature.pageWidth);

            const deltaLeft =
              (parseFloat(target.getAttribute('data-x')) || 0) / pageRatio;
            const deltaBottom =
              (parseFloat(target.getAttribute('data-y')) || 0) / pageRatio;

            const newLeft = signature.signatureDateLeft + deltaLeft;
            const newBottom = signature.signatureDateBottom - deltaBottom;

            signature.signatureDateLeft = newLeft;
            signature.signatureDateBottom = newBottom;

            target.setAttribute('data-x', 0);
            target.setAttribute('data-y', 0);
            target.style.transform = 'none';

            if (this.currentForm.form_id === 'other') {
              this.saveCustomFormSignatures(this.currentForm.signatures);
            }

            this.save(true);
          },
        },
      });
    },

    async createPdfDocument(documentPath) {
      const response = await API.integration.downloadDocument({
        companyId: this.currentCompany.id,
        link: documentPath,
      });

      const pdfSrc = response.data.document.data.link;
      const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());
      return PDFDocument.load(pdfBytes);
    },

    async downloadDocumentAsBuffer(documentLink) {
      const response = await API.integration.downloadDocument({
        companyId: this.currentCompany.id,
        link: this.currentCompany.upload_link.concat('/', documentLink),
      });

      const url = response.data.document.data.link;

      return fetch(url).then(res => res.arrayBuffer());
    },

    async download(downloadType) {
      const pdfDoc = await this.createPdfDocument(
        this.taxReturn.watermark_enabled
          ? this.taxReturn.watermarked_file
          : this.taxReturn.original_file,
      );

      const zip = new JSZip();

      // Load font for date
      const dateFontUrl = window.location.origin + '/static/fonts/Mali.ttf';
      const dateFontBytes = await fetch(dateFontUrl).then(res =>
        res.arrayBuffer(),
      );

      // Load font for text signature
      const signatureFontUrl =
        window.location.origin + '/static/fonts/Zeyada.ttf';
      const signatureFontBytes = await fetch(signatureFontUrl).then(res =>
        res.arrayBuffer(),
      );

      let esignForms = [];

      esignForms = this.taxReturn.e_sign_forms;

      let index = 1;
      for (const esignForm of esignForms) {
        const hasSignatures =
          esignForm.signatures.length > 0 &&
          esignForm.signatures.some(signature => signature.signatureData);

        // If download type is signed only but the form has no signatures, the no-op
        if (downloadType === 'signed-only' && !hasSignatures) {
          continue;
        }

        const esignPdf = await PDFDocument.create();
        esignPdf.registerFontkit(fontkit);

        const dateFont = await esignPdf.embedFont(dateFontBytes);
        const signatureFont = await esignPdf.embedFont(signatureFontBytes);

        const [esignPage] = await esignPdf.copyPages(pdfDoc, [
          esignForm.page - 1,
        ]);

        for (const signature of esignForm.signatures) {
          if (signature.signatureData) {
            const { width: pageWidth } = esignPage.getSize();
            const ratio = pageWidth / signature.pageWidth;

            if (signature.signatureType === 'image') {
              const image = await esignPdf.embedPng(signature.signatureData);
              const yFromBottom = signature.signatureBottom * ratio;

              esignPage.drawImage(image, {
                x: signature.signatureLeft * ratio,
                y: yFromBottom,
                width: signature.signatureWidth * ratio,
                height: signature.signatureHeight * ratio,
              });
            } else {
              const fontSize = signature.signatureTextSize * ratio;

              this.drawTextWithScaling({
                page: esignPage,
                text: signature.signatureData,
                font: signatureFont,
                fontSize,
                boxLeft: signature.signatureLeft * ratio,
                boxWidth: signature.signatureWidth * ratio,
                boxBottom: signature.signatureBottom * ratio,
                boxHeight: signature.signatureHeight * ratio,
                verticalOffset: 0.7,
              });
            }

            if (signature.signatureDate) {
              const dateFontSize = (signature.signatureTextSize * ratio) / 2;
              const dateText = signature.signatureDate;
              const dateTextWidth = dateFont.widthOfTextAtSize(
                dateText,
                dateFontSize,
              );
              const dateTextHeight = dateFont.heightAtSize(dateFontSize);

              const dateBoxWidth = signature.signatureDateWidth * ratio;
              const dateX =
                signature.signatureDateLeft * ratio +
                (dateBoxWidth - dateTextWidth) / 2;
              const dateY =
                signature.signatureDateBottom * ratio + dateTextHeight;

              esignPage.drawText(dateText, {
                x: dateX,
                y: dateY,
                size: dateFontSize,
                font: dateFont,
                color: rgb(0, 0, 0),
              });
            }
          }
        }

        esignPdf.addPage(esignPage);
        const esignData = await esignPdf.save();

        const formDefinition = this.getFormDefinitionById(esignForm.form_id);
        const fileName = `${index}-${
          esignForm.form_id === 'other' && !isEmpty(esignForm.custom_form_name)
            ? esignForm.custom_form_name
            : formDefinition
            ? formDefinition.name
            : esignForm.form_id
        }.pdf`;

        index++;

        zip.file(fileName, esignData, { binary: true });

        if (esignForm.wetSignatures) {
          for (const wetSignature of esignForm.wetSignatures) {
            const signedDocument = await this.downloadDocumentAsBuffer(
              wetSignature.signedDocumentPath,
            );

            // Prefix the file name with an index to prevent files from being overwritten
            const fileName =
              index +
              '-' +
              this.getDocumentName(wetSignature.signedDocumentPath);

            index++;

            zip.file(fileName, signedDocument, { binary: true });
          }
        }
      }

      const zipName =
        this.taxReturn.tax_return_name.replace('.pdf', '') +
        ' (E-sign forms).zip';

      zip.generateAsync({ type: 'blob' }).then(content => {
        download(content, zipName, 'application/zip');
      });
    },

    drawTextWithScaling({
      page,
      text,
      font,
      fontSize,
      boxLeft,
      boxWidth,
      boxBottom,
      boxHeight,
      verticalOffset,
    }) {
      // Calculate exact text dimensions
      const textWidth = font.widthOfTextAtSize(text, fontSize);
      const textHeight = font.heightAtSize(fontSize);

      // Ensure text fits within the box
      const maxTextWidth = boxWidth * 0.9; // Leave 10% margin
      const maxTextHeight = boxHeight * 0.8; // Leave 20% margin

      // Adjust font size if text is too wide
      let adjustedFontSize = fontSize;
      if (textWidth > maxTextWidth) {
        adjustedFontSize = fontSize * (maxTextWidth / textWidth);
      }

      // Recalculate dimensions with adjusted font size
      const adjustedTextWidth = font.widthOfTextAtSize(text, adjustedFontSize);
      const adjustedTextHeight = font.heightAtSize(adjustedFontSize);

      // Position text with exact centering
      const textX = boxLeft + (boxWidth - adjustedTextWidth) / 2;
      // Calculate vertical offset based on provided parameter
      const verticalOffsetPx = Math.min(
        adjustedTextHeight * verticalOffset,
        maxTextHeight * (verticalOffset / 2),
      );
      const textY =
        boxBottom + (boxHeight - adjustedTextHeight) / 2 + verticalOffsetPx;

      page.drawText(text, {
        x: textX,
        y: textY,
        size: adjustedFontSize,
        font,
      });
    },

    // Please don't forget to update ESignStep too if you change this method
    async updatePdfPageDimensions() {
      if (!this.pdfDoc) return;

      const page = await this.pdfDoc.getPage(this.pdfPageIndex);
      const viewport = page.getViewport({ scale: 1.5 });

      this.pdfPageWidth = viewport.width;
      this.pdfPageHeight = viewport.height;
    },

    next() {
      this.$emit('next');
    },

    async markCurrentFormAsReviewed() {
      this.currentForm.reviewed_by = this.currentUser.id;
      await this.save();
    },

    getUnreviewedForms() {
      return this.taxReturn.e_sign_forms.filter(form => {
        return !form.reviewed_by;
      });
    },

    hasUnreviewedForms() {
      return this.getUnreviewedForms().length > 0;
    },

    hasErrors() {
      return this.taxReturn.e_sign_forms.some(form => {
        return this.isFormInvalid(form);
      });
    },

    isFormInvalid(form) {
      return form.signatures.some(signature => {
        if (signature.userId === null) {
          return true;
        } else if (isEmpty(signature.name)) {
          return true;
        } else {
          return false;
        }
      });
    },

    async save(forceSave = false) {
      if (!this.editable && !forceSave) {
        return;
      }

      try {
        this.saving = true;
        const response = await axios.patch(
          `/tax_returns/${this.taxReturn.id}`,
          {
            ...this.taxReturn,
            e_sign_forms: this.taxReturn.e_sign_forms,
          },
        );
        return response.data;
      } finally {
        this.saving = false;
      }
    },

    async saveCustomFormSignatures(signatures) {
      const firmCustomFormName = this.customFormNames.find(
        customFormName =>
          customFormName.name === this.currentForm.custom_form_name,
      );

      const signaturesToSave = uniqBy(
        [
          ...signatures,
          ...(firmCustomFormName.signatures || []), // Make sure we also keep the existing signatures (Spouse's signature box)
        ],
        'title',
      );

      const response = await API.firm.updateCustomFormName({
        firmId: this.currentUserFirm.id,
        id: firmCustomFormName.id,
        signatures: signaturesToSave.map(signature => {
          return pick(signature, [
            'title',
            'pageWidth',
            'pageHeight',
            'signatureWidth',
            'signatureHeight',
            'signatureBottom',
            'signatureLeft',
            'signatureDateWidth',
            'signatureDateHeight',
            'signatureDateLeft',
            'signatureDateBottom',
            'signatureTextSize',
          ]);
        }),
      });

      await this.getCustomFormNames();
    },

    async finishReviewing(skipUnreviewed = false) {
      if (this.hasErrors()) {
        this.erroredFormsWarningDialogOpened = true;
        this.shouldHighlightErroredForms = true;
        this.validateInputs();
        return;
      }

      if (!skipUnreviewed && this.hasUnreviewedForms()) {
        this.unreviewedFormsWarningDialogOpened = true;
        this.shouldHighlightUnreviewedForms = true;
        return;
      }

      const taxReturn = await this.save();
      this.$emit('e-sign-forms-updated', taxReturn);
    },

    validateInputs() {
      this.$refs.form.validate();
    },

    async getCustomFormNames() {
      const response = await API.firm.getCustomFormNames({
        firmId: this.currentUserFirm.id,
      });

      this.customFormNames = response.data;
    },

    async handleCustomFormChange(item) {
      // Depends on whether item is selected or typed, it can be either an object or just a string;
      const name = item && item.name ? item.name : item;
      this.currentForm.custom_form_name = name;

      if (isEmpty(name)) {
        return;
      }

      const customFormName = this.customFormNames.find(
        customFormName => customFormName.name === name,
      );

      if (!customFormName) {
        const response = await API.firm.addCustomFormName({
          firmId: this.currentUserFirm.id,
          name,
        });

        this.customFormNames.push(response.data);
      } else {
        // Initialize saved signatures
        const response = await API.firm.getInitialSignatures({
          firmId: this.currentUserFirm.id,
          id: customFormName.id,
          companyId: this.currentCompany.id,
        });

        this.currentForm.signatures = response.data;
      }
    },

    async deleteCustomFormName(item) {
      const response = await API.firm.deleteCustomFormName({
        firmId: this.currentUserFirm.id,
        id: item.id,
      });

      this.customFormNames = this.customFormNames.filter(
        customFormName => customFormName.id !== item.id,
      );
    },

    async renderPdfWithAnnotations() {
      if (!this.pdfDoc) return;

      const page = await this.pdfDoc.getPage(this.pdfPageIndex);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: context,
        viewport,
      }).promise;

      const container = document.getElementById('pdf-annotation');
      container.innerHTML = '';
      container.appendChild(canvas);

      // Store the actual rendered width (at 1.5 scale)
      this.pdfPageWidth = viewport.width;
      this.pdfPageHeight = viewport.height;

      this.pdfLoaded = true;
    },
  },

  watch: {
    async currentFormIndex() {
      if (this.currentForm) {
        this.pdfPageIndex = this.currentForm.page;

        await this.markCurrentFormAsReviewed();

        if (this.shouldHighlightErroredForms) {
          this.validateInputs();
        }

        await this.renderPdfWithAnnotations();
      }
    },

    async pdfLoaded(value) {
      if (value) {
        await this.markCurrentFormAsReviewed();
        this.updatePdfPageDimensions();
      }
    },
  },

  async created() {
    await this.getFormDefinitions();
  },

  async beforeDestroy() {
    await this.save();
    window.removeEventListener('resize', this.updatePdfPageDimensions);
  },

  async mounted() {
    this.getCustomFormNames();

    try {
      this.pdfLoading = true;

      // Step 1: Fetch the document from API
      const response = await API.integration.downloadDocument({
        companyId: this.currentCompany.id,
        link: this.taxReturn.original_file,
      });

      if (!response.data.document) {
        console.error('No document data found.');
        return;
      }

      const pdfSrc = response.data.document.data.link;

      // Step 2: Load PDF using pdf.js
      const pdflib = window.pdfjsLib;
      pdflib.GlobalWorkerOptions.workerSrc =
        'https://unpkg.com/pdfjs-dist@4.0.379/build/pdf.worker.mjs';

      const loadingTask = pdflib.getDocument({ url: pdfSrc });
      this.pdfDoc = await loadingTask.promise; // Store pdf.js document instance

      // Step 3: Extract pages and render them properly

      // Step 4: Set the page index
      this.pdfPageIndex = this.taxReturn.e_sign_forms[
        this.currentFormIndex - 1
      ].page;
      await this.renderPdfWithAnnotations();
      this.updatePdfPageDimensions();

      // Handle resizing events
      window.addEventListener('resize', this.updatePdfPageDimensions);
      this.makeDraggable();

      // Step 5: Ensure interactive fields (annotations, signatures) are rendered
      if (this.taxReturn.status === 'action_required') {
        this.analyzeAllESignForms();
      }
    } catch (error) {
      console.error('Error loading PDF:', error);
    } finally {
      this.pdfLoading = false;
    }
  },
};
</script>

<style lang="postcss" scoped>
.sc-alert {
  position: sticky;
  top: 0;

  margin: 0px;
  padding: 8px;

  background-color: var(--blue-600);
  color: var(--gray-0);
}

.tab-content-container {
  flex-direction: column;
  background-color: #ebebeb;
}

.instruction {
  margin: 0px;
  padding: 3px 16px;
  border: none;
}

.sidebar {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 20px;
  max-height: calc(100dvh - 120px);
  min-height: calc(100dvh - 120px);
  min-width: 260px;
  max-width: 260px;
  overflow: auto;
  padding: 20px 20px 32px;

  background-color: var(--gray-100);
  border-right: 1px solid var(--gray-300);
}

@media (max-width: 860px) {
  .sidebar {
    display: none;
  }
}

.pdf-container {
  flex: auto;
  position: relative;
  min-height: calc(100dvh - 120px);
  max-height: calc(100dvh - 120px);
}

.toolbar {
  box-sizing: border-box;
  align-items: center;
  display: flex;
  gap: 16px;
  height: 56px;
  justify-content: space-between;
  padding: 0 8px;
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: var(--gray-100);
  border-bottom: 1px solid var(--gray-200);
}

.toolbar__pageNavigation {
  display: flex;
  justify-content: center;
  min-width: calc(100% - 360px);
}

.toolbar__pageActions {
  width: 360px;
}

.toolbar__downloadMenu {
  z-index: 1000;
}

.toolbar input {
  height: 30px;
  padding: 0;
  width: 40px;

  border: 0;
  border-radius: 2px;
  outline: 1px solid var(--gray-300);
  background-color: var(--gray-0);

  font-family: var(--font-family-sans);
  line-height: 32px;
  text-align: center;
}

.toolbar input:active,
.toolbar input:focus {
  outline: 1px solid var(--blue-400);
}

.page-container {
  flex: auto;
  width: 100%;
  height: 100%;
  min-height: calc(100dvh - 176px);
  max-height: calc(100dvh - 176px);
}

.scrollable-container {
  overflow: auto;
  width: 0;

  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.15);
}

.pdf-page-container {
  position: relative;

  flex-direction: column;
  height: max-content;
  max-width: 918px;
  min-width: 918px;
  width: 100%;
}

.pdf {
  position: relative;

  padding: 0;
  max-width: 918px;
  min-width: 918px;
  width: 100%;
}

.page-list {
  padding-left: 0px;
}

.page-list li {
  padding: 4px;
  list-style: none;
}

.page-list li a {
  color: #444;
  text-decoration: none;
}

.page-list li a i {
  color: #444;
}

.page-list li.selected {
  background-color: rgba(0, 0, 0, 0.07);
  border-radius: 4px;
}

.page-list li.errored a,
.page-list li.errored a i,
.page-list li.unreviewed a,
.page-list li.unreviewed a i {
  color: var(--red-700);
}

.page-input >>> .v-input__control > .v-input__slot {
  border-radius: 4px;
  box-shadow: none !important;
  border: 1px solid #bbb;
}

.page-input >>> .v-input__control {
  min-height: 20px;
}

.page-input >>> input {
  width: 32px;
}

.page-input >>> input::-webkit-outer-spin-button,
.page-input >>> input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}

.sidebar--right {
  box-sizing: border-box;
  overflow-y: auto;
  max-width: 320px;
  min-width: 320px;
  padding: 16px;

  background-color: var(--gray-100);
  border-left: 1px solid var(--gray-200);
}

.select-input {
  border: 1px solid #ccc;

  & >>> .v-input__slot {
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.15) !important;
  }

  & >>> .v-input__control {
    min-height: 40px !important;
  }
}

.signer-container {
  margin-top: 12px;
  background-color: #e2e2e2;
  border: 1px solid #ccc;
  padding: 16px;
  border-radius: 4px;
}

.label {
  font-size: 15px;
  min-width: 60px;
}

.form-control {
  margin-bottom: 10px;

  & >>> .v-input {
    border: 1px solid #ccc;
  }

  & >>> .v-input__slot {
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.15) !important;
  }

  & >>> .v-input__control {
    min-height: 20px !important;
  }

  & >>> .v-select__selections {
    overflow: hidden;
  }

  & >>> .v-select__selection--comma {
    flex: auto;
    width: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.form-control .error--text {
  &.v-input {
    border: 1px solid var(--red-500);
  }
}

.signature-container,
.signature-date-container {
  position: absolute;

  & >>> .sign-area {
    display: flex;
    flex-direction: column;
    height: 100%;

    background-color: rgba(76, 175, 80, 0.85);
    border: 2px dashed rgba(0, 0, 0, 0.75);
    border-radius: 4px;
    color: #fff;
    backdrop-filter: blur(0.5px);

    font-size: 1em;
    font-weight: bold;
    text-align: left;
    text-shadow: 0 1px 0 rgba(0, 0, 0, 0.25);
    user-select: none;
  }

  & >>> .sign-area .signer-title {
    position: absolute;
    top: -32px;
    left: -2px;

    padding: 2px 16px;

    background-color: rgba(40, 50, 80, 0.9);
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.5);
    color: rgba(255, 255, 255, 0.75);

    font-size: 14px !important;
    font-weight: medium;
    line-height: 24px !important;
  }

  & >>> .sign-area h2 {
    display: flex;
    flex: auto;
    align-items: center;
    padding: 0px 16px;
  }

  & >>> .sign-area .signer-title {
    padding: 0px 16px;
    flex: auto;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  & >>> .signature-text {
    font-family: 'Zeyada', cursive;
    /* Make sure the signature text appear in one line */
    white-space: nowrap;
  }

  & >>> .signature-date {
    font-family: 'Mali', cursive;
    /* Make sure the signature date appear in one line */
    white-space: nowrap;
  }
}
</style>
